import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5300/blummental/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

class ApiService {
  // Auth endpoints
  async login(data) {
    try {
      const response = await api.post("/auth/login", data);
      if (response.data.success && response.data.token) {
        localStorage.setItem("authToken", response.data.token);
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Login failed");
    }
  }

  async register(data) {
    try {
      const response = await api.post("/auth/register", data);
      if (response.data.success && response.data.token) {
        localStorage.setItem("authToken", response.data.token);
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Registration failed");
    }
  }

  async logout() {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("authToken");
    }
  }

  async getCurrentUser() {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to get user");
    }
  }

  async updateProfile(data) {
    try {
      const response = await api.put("/auth/update-profile", data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Profile update failed");
    }
  }

  async changePassword(data) {
    try {
      const response = await api.put("/auth/change-password", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Password change failed"
      );
    }
  }

  // Task endpoints
  async getTasks(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === "object" && key === "dateRange") {
            params.append("startDate", value.start);
            params.append("endDate", value.end);
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/tasks?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch tasks");
    }
  }

  async getTask(id) {
    try {
      const response = await api.get(`/tasks/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch task");
    }
  }

  async createTask(data) {
    try {
      const response = await api.post("/tasks", data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to create task");
    }
  }

  async updateTask(id, data) {
    try {
      const response = await api.put(`/tasks/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to update task");
    }
  }

  async deleteTask(id) {
    try {
      const response = await api.delete(`/tasks/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to delete task");
    }
  }

  async startTask(id) {
    try {
      const response = await api.post(`/tasks/${id}/start`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to start task");
    }
  }

  async pauseTask(id) {
    try {
      const response = await api.post(`/tasks/${id}/pause`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to pause task");
    }
  }

  async completeTask(id, completionNotes) {
    try {
      const response = await api.post(`/tasks/${id}/complete`, {
        completionNotes,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to complete task"
      );
    }
  }

  async getTaskAnalytics() {
    try {
      const response = await api.get("/tasks/analytics");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch analytics"
      );
    }
  }

  async uploadTaskAttachment(id, file) {
    try {
      const formData = new FormData();
      formData.append("files", file);

      const response = await api.post(`/tasks/${id}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to upload attachment"
      );
    }
  }

  async uploadTaskImages(id, formData) {
    try {
      const response = await api.post(`/tasks/${id}/images`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to upload images"
      );
    }
  }

  // Car endpoints
  async getCars(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      console.log('Fetching cars with params:', params.toString());
      const response = await api.get(`/cars?${params.toString()}`);
      console.log('Cars API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getCars API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch cars');
    }
  }

  // Simple cars endpoint for mobile apps
  async getCarsSimple() {
    try {
      console.log('Fetching cars (simple endpoint for mobile)');
      const response = await api.get('/cars/simple');
      console.log('Simple cars API response:', response.data);
      return response.data;
    } catch (error) {
      console.log('Simple endpoint not available, falling back to regular endpoint');
      // Fallback to regular endpoint if simple endpoint doesn't exist
      return this.getCars();
    }
  }

  async getCar(id) {
    try {
      const response = await api.get(`/cars/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch car");
    }
  }

  async searchCarByPlate(numberPlate) {
    try {
      const response = await api.get(`/cars/search/${numberPlate}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Car not found");
    }
  }

  async createCar(data) {
    try {
      const response = await api.post("/cars", data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to create car");
    }
  }

  async updateCar(id, data) {
    try {
      const response = await api.put(`/cars/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to update car");
    }
  }

  async deleteCar(id) {
    try {
      const response = await api.delete(`/cars/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to delete car");
    }
  }

  async addServiceRecord(id, data) {
    try {
      const response = await api.post(`/cars/${id}/service`, data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to add service record"
      );
    }
  }

  async getServiceHistory(id) {
    try {
      const response = await api.get(`/cars/${id}/service-history`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch service history"
      );
    }
  }

  async getMaintenanceDue() {
    try {
      const response = await api.get("/cars/maintenance-due");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch maintenance data"
      );
    }
  }

  async uploadCarImages(id, formData) {
    try {
      console.log('Uploading images for car ID:', id);
      const response = await api.post(`/cars/${id}/images`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log('Image upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('uploadCarImages API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to upload car images');
    }
  }

  async deleteCarImage(carId, imageId) {
    try {
      const response = await api.delete(`/cars/${carId}/images/${imageId}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to delete car image"
      );
    }
  }

  async setPrimaryCarImage(carId, imageId) {
    try {
      const response = await api.put(
        `/cars/${carId}/images/${imageId}/primary`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to set primary image"
      );
    }
  }

  async importCarsFromExcel(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/cars/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to import Excel file"
      );
    }
  }

  // Car Documents
  async uploadCarDocuments(carId, files, metadata) {
    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append("files", file);
        if (metadata[index]) {
          formData.append(`name_${index}`, metadata[index].name || file.name);
          formData.append(`type_${index}`, metadata[index].type || "other");
          formData.append(
            `description_${index}`,
            metadata[index].description || ""
          );
        }
      });

      const response = await api.post(`/cars/${carId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to upload documents"
      );
    }
  }

  async deleteCarDocument(carId, documentId) {
    try {
      const response = await api.delete(
        `/cars/${carId}/documents/${documentId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to delete document"
      );
    }
  }

  // Car Work Logs
  async getCarWorkLogs(carId) {
    try {
      const response = await api.get(`/cars/${carId}/work-logs`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch work logs"
      );
    }
  }

  async addCarWorkLog(carId, workLogData) {
    try {
      const response = await api.post(`/cars/${carId}/work-logs`, workLogData);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to add work log"
      );
    }
  }

  // User endpoints
  async getUsers(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch users");
    }
  }

  async getMechanics() {
    try {
      const response = await api.get("/users/mechanics");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch mechanics"
      );
    }
  }

  async getUser(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch user");
    }
  }

  async createUser(data) {
    try {
      const response = await api.post("/users", data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to create user");
    }
  }

  async updateUser(id, data) {
    try {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to update user");
    }
  }

  async deleteUser(id) {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to delete user");
    }
  }

  // Profile management methods
  async createUserWithProfile(formData) {
    try {
      const response = await api.post('/users', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create user profile');
    }
  }

  async updateUserProfile(id, formData) {
    try {
      const response = await api.put(`/users/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update user profile');
    }
  }

  async getUserDocuments(userId) {
    try {
      const response = await api.get(`/users/${userId}/documents`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user documents');
    }
  }

  async deleteUserDocument(userId, documentId) {
    try {
      const response = await api.delete(`/users/${userId}/documents/${documentId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete document');
    }
  }

  async getDocumentUrl(userId, documentId) {
    try {
      const response = await api.get(`/users/${userId}/documents/${documentId}/url`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get document URL');
    }
  }

  async bulkUpdateEmploymentStatus(userIds, employmentStatus) {
    try {
      const response = await api.patch('/users/bulk/status', {
        userIds,
        employmentStatus
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update employment status');
    }
  }

  async getUserProfileStats() {
    try {
      const response = await api.get('/users/stats/profiles');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch profile statistics');
    }
  }

  async resetUserPassword(userId, newPassword) {
    try {
      const response = await api.put(`/users/${userId}/reset-password`, {
        newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset user password');
    }
  }

  // Company settings endpoints
  async getCompanySettings() {
    try {
      const response = await api.get('/company');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch company settings');
    }
  }

  async updateCompanySettings(formData) {
    try {
      const response = await api.post('/company', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update company settings');
    }
  }

  async deleteCompanyLogo() {
    try {
      const response = await api.delete('/company/logo');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete company logo');
    }
  }

  async testLocation(latitude, longitude) {
    try {
      const response = await api.post('/company/test-location', {
        latitude,
        longitude
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to test location');
    }
  }

  async getCompanyGeofence() {
    try {
      const response = await api.get('/company/geofence');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch company geofence');
    }
  }

  async getCompanyPublicInfo() {
    try {
      const response = await api.get('/company/public');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch company information');
    }
  }

  // Dashboard endpoints
  async getAdminDashboard() {
    try {
      const response = await api.get("/dashboard/admin");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch admin dashboard"
      );
    }
  }

  async getMechanicDashboard() {
    try {
      const response = await api.get("/dashboard/mechanic");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch mechanic dashboard"
      );
    }
  }

  async getStats(period = "month") {
    try {
      const response = await api.get(`/dashboard/stats?period=${period}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch statistics"
      );
    }
  }

  // Group endpoints
  async getGroups(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/groups?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch groups"
      );
    }
  }

  async getGroup(id) {
    try {
      const response = await api.get(`/groups/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Failed to fetch group");
    }
  }

  async createGroup(data) {
    try {
      const response = await api.post("/groups", data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to create group"
      );
    }
  }

  async updateGroup(id, data) {
    try {
      const response = await api.put(`/groups/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to update group"
      );
    }
  }

  async deleteGroup(id) {
    try {
      const response = await api.delete(`/groups/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to delete group"
      );
    }
  }

  async addGroupMembers(groupId, memberIds) {
    try {
      const response = await api.post(`/groups/${groupId}/members`, {
        memberIds,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to add group members"
      );
    }
  }

  async removeGroupMember(groupId, memberId) {
    try {
      const response = await api.delete(
        `/groups/${groupId}/members/${memberId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to remove group member"
      );
    }
  }

  async getGroupTasks(groupId, options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(
        `/groups/${groupId}/tasks?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Failed to fetch group tasks"
      );
    }
  }

  // Template endpoints
  async getTemplates(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/templates?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch templates');
    }
  }

  async getTemplate(id) {
    try {
      const response = await api.get(`/templates/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch template');
    }
  }

  async createTemplate(data) {
    try {
      const response = await api.post('/templates', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create template');
    }
  }

  async updateTemplate(id, data) {
    try {
      const response = await api.put(`/templates/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update template');
    }
  }

  async duplicateTemplate(id, newName) {
    try {
      const response = await api.post(`/templates/${id}/duplicate`, { name: newName });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to duplicate template');
    }
  }

  async deleteTemplate(id) {
    try {
      const response = await api.delete(`/templates/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete template');
    }
  }

  async incrementTemplateUsage(id) {
    try {
      const response = await api.post(`/templates/${id}/use`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to increment template usage');
    }
  }

  async getTemplateOptions() {
    try {
      const response = await api.get('/templates/meta/options');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch template options');
    }
  }

  // Template-based task creation
  async createTaskFromTemplate(templateId, taskData, templateData) {
    try {
      const response = await api.post('/tasks/from-template', {
        templateId,
        taskData,
        templateData
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create task from template');
    }
  }

  // Form Submission endpoints
  async getSubmissions(options = {}) {
    try {
      const params = new URLSearchParams();
      Object.entries(options).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/submissions?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch form submissions');
    }
  }

  async getSubmission(id) {
    try {
      const response = await api.get(`/submissions/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch form submission');
    }
  }

  async createSubmission(submissionData) {
    try {
      const response = await api.post('/submissions', submissionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create form submission');
    }
  }

  async updateSubmission(id, submissionData) {
    try {
      const response = await api.put(`/submissions/${id}`, submissionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update form submission');
    }
  }

  async deleteSubmission(id) {
    try {
      const response = await api.delete(`/submissions/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete form submission');
    }
  }

  async submitForm(id) {
    try {
      const response = await api.post(`/submissions/${id}/submit`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit form');
    }
  }

  async uploadSubmissionImages(id, files) {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.post(`/submissions/${id}/upload-images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload images');
    }
  }

  async getSubmissionStats() {
    try {
      const response = await api.get('/submissions/meta/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch submission statistics');
    }
  }

  // Attendance endpoints
  async getAttendanceStatus() {
    try {
      const response = await api.get('/attendance/status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get attendance status');
    }
  }

  async checkIn(locationData) {
    try {
      const response = await api.post('/attendance/check-in', locationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check in');
    }
  }

  async checkOut(locationData) {
    try {
      const response = await api.post('/attendance/check-out', locationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check out');
    }
  }

  async startBreak(reason = '') {
    try {
      const response = await api.post('/attendance/break/start', { reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to start break');
    }
  }

  async endBreak() {
    try {
      const response = await api.post('/attendance/break/end');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to end break');
    }
  }

  async getAttendanceHistory(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/attendance/history?${queryString}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get attendance history');
    }
  }

  async getDailySummary(date = null) {
    try {
      const params = date ? `?date=${date}` : '';
      const response = await api.get(`/attendance/summary/daily${params}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get daily summary');
    }
  }

  async getAdminDailyAttendance(date = null) {
    try {
      const params = date ? `?date=${date}` : '';
      const response = await api.get(`/attendance/admin/daily${params}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get admin daily attendance');
    }
  }

  async validateLocation(latitude, longitude) {
    try {
      const response = await api.post('/attendance/validate-location', {
        latitude,
        longitude
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to validate location');
    }
  }

  async getGeofences() {
    try {
      const response = await api.get('/attendance/geofences');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get geofences');
    }
  }

  // Admin Geofence Management
  async getAllGeofences(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/geofences?${queryString}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get geofences');
    }
  }

  async createGeofence(geofenceData) {
    try {
      const response = await api.post('/geofences', geofenceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create geofence');
    }
  }

  async updateGeofence(id, geofenceData) {
    try {
      const response = await api.put(`/geofences/${id}`, geofenceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update geofence');
    }
  }

  async deleteGeofence(id) {
    try {
      const response = await api.delete(`/geofences/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete geofence');
    }
  }

  async setDefaultGeofence(id) {
    try {
      const response = await api.patch(`/geofences/${id}/set-default`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to set default geofence');
    }
  }

  async testGeofenceLocation(id, latitude, longitude) {
    try {
      const response = await api.post(`/geofences/${id}/test-location`, {
        latitude,
        longitude
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to test geofence location');
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await api.get("/health");
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Health check failed");
    }
  }
}

export default new ApiService();
